import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Calendar, Users, FileText, Zap } from "lucide-react";

const Hero = () => {
  const nav = useNavigate();

  return (
    <section className="relative overflow-hidden bg-white pt-28 pb-24 md:pt-36 md:pb-32">
      {/* soft background accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full bg-[#14B8A6]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-[520px] h-[520px] rounded-full bg-[#0F2A5F]/10 blur-3xl" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#E6F7F5] border border-[#14B8A6]/20 text-[#0F2A5F] px-3 py-1.5 rounded-full text-xs font-semibold mb-6">
            <Zap size={13} className="text-[#14B8A6]" />
            Hi-Octane Practice • Purpose-built for Dentists
          </div>

          <h1
            className="text-4xl md:text-5xl lg:text-[56px] font-extrabold leading-[1.08] text-[#0F2A5F] mb-6"
            style={{ fontFamily: "'Open Sans', sans-serif" }}
          >
            Run your clinic at <span className="text-[#14B8A6]">full throttle</span>.
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
            Dotmedy is the modern practice-management platform for dentists.
            Manage patients, dental charts, appointments and billing — all from
            a single, beautifully-crafted workspace.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button
              onClick={() => nav("/register")}
              className="inline-flex items-center justify-center gap-2 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors shadow-lg shadow-[#14B8A6]/20 hover:shadow-xl"
            >
              Start free — no card <ArrowRight size={16} />
            </button>
            <button
              onClick={() => nav("/login")}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2A5F] border border-slate-200 font-semibold px-6 py-3.5 rounded-lg transition-colors hover:border-[#0F2A5F]"
            >
              Doctor Sign in
            </button>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            {["FDI dental chart", "WhatsApp reminders", "GST invoicing", "UHID search"].map((f) => (
              <div key={f} className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={15} className="text-[#14B8A6]" />
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Visual: floating dashboard mock */}
        <div className="relative">
          <div className="relative rounded-2xl bg-gradient-to-br from-[#0F2A5F] to-[#14B8A6] p-1.5 shadow-2xl shadow-[#0F2A5F]/25">
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-100">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <span className="ml-3 text-[11px] text-slate-500 font-mono">dotmedy.com/app</span>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Today</div>
                    <div className="text-lg font-bold text-[#0F2A5F]">Good morning, Dr. Anaya</div>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-[#E6F7F5] flex items-center justify-center text-[#14B8A6] font-bold text-sm">A</div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    { icon: Users, label: "Patients", val: "1,284", c: "#14B8A6" },
                    { icon: Calendar, label: "Today", val: "12", c: "#0F2A5F" },
                    { icon: FileText, label: "Bills", val: "₹68k", c: "#14B8A6" },
                  ].map((s, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                      <s.icon size={14} style={{ color: s.c }} />
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-1.5">{s.label}</div>
                      <div className="text-sm font-bold text-slate-800">{s.val}</div>
                    </div>
                  ))}
                </div>

                <div className="border border-slate-100 rounded-lg p-2.5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Upcoming Appointments</div>
                  <div className="space-y-1.5">
                    {[
                      { t: "10:00", n: "Rohan Kapoor", r: "Root canal follow-up" },
                      { t: "11:30", n: "Priya Menon", r: "Cleaning" },
                      { t: "14:15", n: "Aditya Sharma", r: "Cavity filling" },
                    ].map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[#14B8A6] font-semibold">{a.t}</span>
                          <span className="text-slate-700 font-medium">{a.n}</span>
                        </div>
                        <span className="text-slate-400 text-[10px]">{a.r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* floating badge */}
          <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl border border-slate-100 px-4 py-3 hidden sm:flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E6F7F5] text-[#14B8A6] flex items-center justify-center">
              <CheckCircle2 size={20} />
            </div>
            <div>
              <div className="text-xs text-slate-500">Appointment sent</div>
              <div className="text-sm font-semibold text-[#0F2A5F]">via WhatsApp ✓</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
